import React, { useState, useCallback, useEffect } from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import { useIsMobile } from '@/hooks/use-mobile';
import { useEvalDatasets } from '@/hooks/useEvaluator';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Plus, UsersRound } from 'lucide-react';
import { DataSurface } from '@/components/platform/DataSurface';
import { DatasetBrowser } from '@/components/evaluator/DatasetBrowser';
import { DatasetItemsList } from '@/components/evaluator/DatasetItemsList';
import { DatasetItemDetail } from '@/components/evaluator/DatasetItemDetail';
import { GeneratePersonasDialog } from '@/components/evaluator/GeneratePersonasDialog';

interface EvaluatorContentProps {
  hideHeaderAction?: boolean;
  generateOpen?: boolean;
  onGenerateOpenChange?: (open: boolean) => void;
}

export const EvaluatorContent: React.FC<EvaluatorContentProps> = ({
  hideHeaderAction = false,
  generateOpen: controlledGenerateOpen,
  onGenerateOpenChange,
}) => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const isMobile = useIsMobile();

  const { data, isLoading } = useEvalDatasets(orgId);
  const datasets = data?.datasets ?? [];
  const hasDatasets = datasets.length > 0;

  const [selectedDataset, setSelectedDataset] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<'sidebar' | 'center'>('sidebar');
  const [internalGenerateOpen, setInternalGenerateOpen] = useState(false);
  const generateOpen = controlledGenerateOpen ?? internalGenerateOpen;
  const setGenerateOpen = onGenerateOpenChange ?? setInternalGenerateOpen;

  useEffect(() => {
    if (!isMobile) setMobileView('sidebar');
  }, [isMobile]);

  useEffect(() => {
    if (selectedDataset || datasets.length === 0) return;
    const sorted = [...datasets].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
    setSelectedDataset(sorted[0]?.name ?? null);
  }, [datasets, selectedDataset]);

  const handleSelectDataset = useCallback((name: string) => {
    setSelectedDataset(name);
    setSelectedItemId(null);
    if (isMobile) setMobileView('center');
  }, [isMobile]);

  const handleSelectItem = useCallback((itemId: string) => {
    setSelectedItemId(itemId);
  }, []);

  const handleBackToItems = useCallback(() => {
    setSelectedItemId(null);
  }, []);

  const handleDatasetDeleted = useCallback(() => {
    setSelectedDataset(null);
    setSelectedItemId(null);
  }, []);

  const renderCenter = () => {
    if (selectedDataset && selectedItemId && orgId) {
      return (
        <div className="flex-1 overflow-auto p-5">
          <DatasetItemDetail
            orgId={orgId}
            datasetName={selectedDataset}
            itemId={selectedItemId}
            onBack={handleBackToItems}
          />
        </div>
      );
    }

    if (selectedDataset && orgId) {
      return (
        <DatasetItemsList
          orgId={orgId}
          datasetName={selectedDataset}
          onSelectItem={handleSelectItem}
        />
      );
    }

    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Select a population.
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {!hideHeaderAction && orgId ? (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setGenerateOpen(true)}>
            <Plus className="h-4 w-4" />
            Create Population
          </Button>
        </div>
      ) : null}

      {!isLoading && !hasDatasets ? (
        <DataSurface>
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-muted/50">
              <UsersRound className="h-6 w-6 text-muted-foreground/60" />
            </div>
            <h3 className="mb-1 text-sm font-medium text-foreground">No populations yet</h3>
            {orgId && (
              <Button onClick={() => setGenerateOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Create Population
              </Button>
            )}
          </div>
        </DataSurface>
      ) : (
        <DataSurface className="flex h-[calc(100vh-17rem)] min-h-[620px] overflow-hidden">
          {/* Left sidebar - Datasets */}
          {(!isMobile || mobileView === 'sidebar') && (
            <DatasetBrowser
              orgId={orgId}
              selectedDataset={selectedDataset}
              onSelectDataset={handleSelectDataset}
              onDatasetDeleted={handleDatasetDeleted}
            />
          )}

          {/* Center panel - Items / Item detail */}
          {(!isMobile || mobileView === 'center') && (
            <div className="flex flex-1 flex-col overflow-hidden bg-card">
              {isMobile && (
                <div className="flex items-center gap-2 border-b px-4 py-2">
                  <Button variant="ghost" size="sm" onClick={() => setMobileView('sidebar')}>
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Back
                  </Button>
                  <span className="text-sm text-muted-foreground">Personas</span>
                </div>
              )}
              {renderCenter()}
            </div>
          )}
        </DataSurface>
      )}

      {orgId && (
        <GeneratePersonasDialog
          open={generateOpen}
          onOpenChange={setGenerateOpen}
          organizationId={orgId}
        />
      )}
    </div>
  );
};
